/**
 * The vocabulary, with a face.
 *
 * ── What this is, and what it deliberately is not ────────────────────────────
 *
 * Every enumerated value in this application is DEFINED once, on the server or
 * in `shared/`, and this file gives those values their English and Puerto Rican
 * Spanish labels. It re-exports rather than redeclares: a second copy of
 * `STAGES` on the client is a list that drifts, and the drift shows up as a
 * stage the board cannot draw rather than as a build failure.
 *
 * **No rule lives here.** `canTransition`, `stageRank` and `reachableStages`
 * are re-exported from `shared/pipeline.ts` unchanged, so the client greys out
 * exactly the buttons the server refuses (CLAUDE.md rule 14). Nothing in this
 * file decides whether an action is allowed; it decides what a code is called.
 *
 * ── A code we have never seen is not a crash ─────────────────────────────────
 *
 * Reason codes come from `decision_reasons`, which is a TABLE — `shared/
 * pipeline.ts` says so explicitly, because a reason deactivated in the console
 * has to stop being offered and a constant compiled into a bundle cannot know
 * that. So every lookup falls back to English, and then to a humanised form of
 * the code itself. A new seeded reason renders as "Seller unreliable" until
 * somebody translates it, which beats rendering `pass_seller_unreliable` and
 * beats throwing.
 *
 * ── Strings ──────────────────────────────────────────────────────────────────
 *
 * Held here rather than in `src/lib/i18n.ts`, which another work unit owns in
 * this build — the arrangement `Intake.tsx` and `MapScreen.tsx` already use.
 * Each Spanish table is typed against its English one, so a missing
 * translation is a compile error.
 *
 * ── §3.4, §3.6, §6, §7, §10.2, §10.3 ─────────────────────────────────────────
 */

import type { Locale } from './i18n'
import { LANES, LANE_OF, STAGES, type Lane, type Stage } from '../../shared/pipeline'
import type {
  Availability,
  Certainty,
  ConditionGrade,
  DamageFlag,
  FactAttribute,
  Provenance,
  StructureType,
  TitleStatus,
  UtilityKey,
} from '../../shared/listing'
import type { AccessCode } from '../../shared/access'

/* ────────────────────────────────────────────────────────────────────────────
 * Re-exports — the single definition of each vocabulary
 * ────────────────────────────────────────────────────────────────────────── */

export {
  STAGES,
  DISPOSITIONS,
  CLOSED_DISPOSITIONS,
  LANES,
  LANE_OF,
  stagesInLane,
  stageRank,
  isStage,
  isDisposition,
  canTransition,
  requiresReason,
  requiresPurchaseEvidence,
  requiresPlacementEvidence,
  reachableStages,
  PASS_REASONS,
  FINALIST_REASONS,
  LOST_REASONS,
  SCAM_REASONS,
  DUPLICATE_REASONS,
  REOPEN_REASONS,
  SEEDED_REASONS,
} from '../../shared/pipeline'
export type {
  Stage,
  Disposition,
  Lane,
  ReasonKind,
  PipelineState,
  TransitionRequest,
  TransitionOutcome,
} from '../../shared/pipeline'

export {
  STRUCTURE_TYPES,
  CONDITION_GRADES,
  TITLE_STATUSES,
  AVAILABILITIES,
  PROVENANCES,
  CERTAINTIES,
  FACT_ATTRIBUTES,
  UTILITY_KEYS,
  DAMAGE_FLAGS,
  isStructureType,
  isAvailability,
  isProvenance,
  isFactAttribute,
  countsAsLive,
} from '../../shared/listing'
export type {
  StructureType,
  ConditionGrade,
  TitleStatus,
  Availability,
  Provenance,
  Certainty,
  FactAttribute,
  UtilityKey,
  DamageFlag,
} from '../../shared/listing'

export { assessAccess, rollUp, DEFAULT_ACCESS_TUNING } from '../../shared/access'
export type {
  AccessFinding,
  AccessCode,
  AccessLevel,
  AccessRollUp,
  AccessSeverity,
  LandAccessProfile,
  MoveEnvelope,
} from '../../shared/access'

export { formatPrForDisplay, maskDigits, normalizePhone } from '../../shared/phone'

/**
 * The badge vocabulary is presentation, so it is defined in `format.ts` — and
 * named here as well, because §10.3 puts `certaintyBadge()` on this module and
 * a caller should not have to know which of the two files it lives in.
 */
export { certaintyBadge } from './format'

/* ────────────────────────────────────────────────────────────────────────────
 * Vocabularies the SERVER owns, mirrored as types only
 *
 * These are string unions rather than re-exports because their definitions
 * live in `services/api/src/*.ts`, which the client does not — and must not —
 * import: those modules pull in `Env`, D1 and the whole Worker runtime. The
 * union is the contract; a value outside it falls back to its humanised code
 * rather than blanking a panel.
 * ────────────────────────────────────────────────────────────────────────── */

/** `cost.ts`'s twelve categories, in the order the breakdown lists them. */
export const COST_CATEGORIES = [
  'purchase',
  'taxes_fees',
  'road_transport_origin',
  'ferry',
  'road_transport_island',
  'handling',
  'permits',
  'repairs',
  'placement',
  'utilities',
  'contingency',
  'other',
] as const
export type CostCategory = (typeof COST_CATEGORIES)[number]

/** `cost.ts`'s bases. `assumption` is the only one a recompute may overwrite. */
export const COST_BASES = ['assumption', 'listing', 'override', 'quote', 'manual'] as const
export type CostBasis = (typeof COST_BASES)[number]

/** `scoring.ts`'s thirteen factors, in weight order. §6.1. */
export const FACTOR_KEYS = [
  'landed_cost_vs_budget',
  'transportability',
  'land_fit',
  'condition',
  'livability',
  'title_and_documentation',
  'price_vs_comparables',
  'repairs_required',
  'distance_and_logistics',
  'age',
  'profile_match',
  'seller_responsiveness',
  'listing_freshness',
] as const
export type FactorKey = (typeof FACTOR_KEYS)[number]

/** `risk.ts`. Only `high` caps the score — §6.3. */
export const RISK_LEVELS = ['low', 'caution', 'high'] as const
export type RiskLevel = (typeof RISK_LEVELS)[number]

/** `risk.ts`'s thirteen rules. */
export const RISK_KEYS = [
  'advance_payment_requested',
  'refuses_inspection',
  'price_far_below_floor',
  'price_far_below_comparables',
  'unusual_payment_method',
  'duplicate_other_seller',
  'stock_or_reused_images',
  'no_title',
  'no_vin_serial',
  'copied_description',
  'inconsistent_location',
  'contradictory_claims',
  'urgency_language',
] as const
export type RiskKey = (typeof RISK_KEYS)[number]

export const DILIGENCE_STATUSES = ['pass', 'fail', 'unknown', 'na'] as const
export type DiligenceStatus = (typeof DILIGENCE_STATUSES)[number]

export const DILIGENCE_CATEGORIES = ['legal', 'structure', 'systems', 'condition', 'transport'] as const
export type DiligenceCategory = (typeof DILIGENCE_CATEGORIES)[number]

export const DOC_TYPES = [
  'title',
  'registration',
  'vin_photo',
  'serial_photo',
  'inspection_photo',
  'listing_photo',
  'seller_screenshot',
  'transport_quote',
  'receipt',
  'agreement',
  'dimensions',
  'manual',
  'repair_estimate',
  'other',
] as const
export type DocType = (typeof DOC_TYPES)[number]

export const QUOTE_LEGS = ['road_origin', 'ferry', 'road_island', 'crane', 'handling', 'full'] as const
export type QuoteLeg = (typeof QUOTE_LEGS)[number]

export const QUOTE_STATUSES = ['requested', 'quoted', 'declined', 'accepted', 'expired'] as const
export type QuoteStatus = (typeof QUOTE_STATUSES)[number]

export const PLAN_STATUSES = ['planning', 'scheduled', 'in_transit', 'delivered', 'placed'] as const
export type PlanStatus = (typeof PLAN_STATUSES)[number]

/** `sellers.ts`'s log channels. `resolveChannel` is the only producer of an href. */
export const LOG_CHANNELS = ['call', 'sms', 'whatsapp', 'marketplace', 'email', 'in_person', 'note'] as const
export type LogChannel = (typeof LOG_CHANNELS)[number]

export const DIRECTIONS = ['inbound', 'outbound', 'internal'] as const
export type Direction = (typeof DIRECTIONS)[number]

export const OUTCOMES = ['reached', 'no_answer', 'voicemail', 'busy', 'wrong_number'] as const
export type ContactOutcome = (typeof OUTCOMES)[number]

/* ────────────────────────────────────────────────────────────────────────────
 * The lookup
 * ────────────────────────────────────────────────────────────────────────── */

type Table<K extends string> = Record<Locale, Record<K, string>>

/**
 * `pass_seller_unreliable` → `Pass seller unreliable`.
 *
 * The last resort, and it is deliberately not "Unknown": a code we have not
 * translated still tells the reader roughly what happened, and a screen full
 * of "Unknown" tells them nothing at all.
 */
export function humanise(code: string): string {
  const words = code.replace(/[_.]+/g, ' ').trim()
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : code
}

function look<K extends string>(table: Table<K>, locale: Locale, key: string): string {
  const localised = table[locale] as Record<string, string | undefined>
  const english = table.en as Record<string, string | undefined>
  return localised[key] ?? english[key] ?? humanise(key)
}

/* ────────────────────────────────────────────────────────────────────────────
 * The pipeline
 * ────────────────────────────────────────────────────────────────────────── */

const STAGE_EN: Record<Stage, string> = {
  new: 'New',
  reviewing: 'Reviewing',
  interested: 'Interested',
  contact_seller: 'Contacting the seller',
  waiting_seller: 'Waiting on the seller',
  qualifying: 'Qualifying',
  transport_quote: 'Transport quote',
  inspection: 'Inspection',
  negotiating: 'Negotiating',
  finalist: 'Finalist',
  purchase_pending: 'Purchase pending',
  purchased: 'Purchased',
  transport_scheduled: 'Transport scheduled',
  in_transit: 'In transit',
  delivered: 'Delivered',
  placed_on_land: 'Placed on the land',
  project_complete: 'Project complete',
}

const STAGE_ES: Record<Stage, string> = {
  new: 'Nuevo',
  reviewing: 'En revisión',
  interested: 'Interesa',
  contact_seller: 'Contactando al vendedor',
  waiting_seller: 'Esperando al vendedor',
  qualifying: 'Calificando',
  transport_quote: 'Cotización de transporte',
  inspection: 'Inspección',
  negotiating: 'Negociando',
  finalist: 'Finalista',
  purchase_pending: 'Compra pendiente',
  purchased: 'Comprado',
  transport_scheduled: 'Transporte agendado',
  in_transit: 'En camino',
  delivered: 'Entregado',
  placed_on_land: 'Colocado en el terreno',
  project_complete: 'Proyecto completo',
}

const STAGES_TABLE: Table<Stage> = { en: STAGE_EN, 'es-PR': STAGE_ES }
export const stageLabel = (stage: string, locale: Locale): string => look(STAGES_TABLE, locale, stage)

const LANE_TABLE: Table<Lane> = {
  en: {
    triage: 'Triage',
    pursuing: 'Pursuing',
    diligence: 'Diligence',
    deciding: 'Deciding',
    landing: 'Landing',
  },
  'es-PR': {
    triage: 'Triaje',
    pursuing: 'En marcha',
    diligence: 'Investigando',
    deciding: 'Decidiendo',
    landing: 'Aterrizando',
  },
}
export const laneLabel = (lane: string, locale: Locale): string => look(LANE_TABLE, locale, lane)

/**
 * `disposition` is orthogonal to `stage`: it is HOW IT ENDED, or `open`.
 *
 * §11's `SOLD` is `lost` here. `sold` survives only as a `listings.availability`
 * value, and the two must never share a word — "someone else bought it" and
 * "the posting says sold" are opposite outcomes.
 */
const DISPOSITION_TABLE: Table<'open' | 'passed' | 'lost' | 'scam' | 'duplicate' | 'complete'> = {
  en: {
    open: 'Open',
    passed: 'Passed',
    lost: 'Lost to someone else',
    scam: 'Scam',
    duplicate: 'Duplicate',
    complete: 'Complete',
  },
  'es-PR': {
    open: 'Abierta',
    passed: 'Descartada',
    lost: 'Se la llevó otro',
    scam: 'Estafa',
    duplicate: 'Duplicada',
    complete: 'Completa',
  },
}
export const dispositionLabel = (disposition: string, locale: Locale): string =>
  look(DISPOSITION_TABLE, locale, disposition)

export interface StageColumn {
  lane: Lane
  label: string
  stages: Stage[]
}

/**
 * The five board columns, each with the stages that fall in it.
 *
 * Derived from `LANE_OF` rather than written out, so adding a stage to
 * `shared/pipeline.ts` puts it on the board automatically instead of silently
 * dropping it off one.
 */
export function stageColumns(locale: Locale): StageColumn[] {
  return LANES.map((lane) => ({
    lane,
    label: laneLabel(lane, locale),
    stages: STAGES.filter((stage) => LANE_OF[stage] === lane),
  }))
}

/* ────────────────────────────────────────────────────────────────────────────
 * Decision reasons — codes that live in a TABLE, not in code
 * ────────────────────────────────────────────────────────────────────────── */

const REASON_EN = {
  pass_too_expensive: 'Too expensive',
  pass_transport_too_expensive: 'Transport costs too much',
  pass_bad_condition: 'Condition is too poor',
  pass_title_issue: 'Title problem',
  pass_too_large: 'Too large',
  pass_too_small: 'Too small',
  pass_poor_access: 'Cannot get it onto the land',
  pass_seller_unreliable: 'Seller is unreliable',
  pass_sold: 'Already sold',
  pass_scam_concern: 'Looks like a scam',
  pass_found_better: 'Found a better one',
  pass_other: 'Other',

  finalist_excellent_landed_cost: 'Excellent landed cost',
  finalist_nearby: 'Close by',
  finalist_good_condition: 'Good condition',
  finalist_easy_transport: 'Easy to move',
  finalist_excellent_size: 'The right size',
  finalist_utilities_included: 'Utilities included',
  finalist_strong_seller: 'Straightforward seller',
  finalist_move_in_ready: 'Move-in ready',

  lost_sold_to_other: 'Sold to someone else',
  lost_seller_withdrew: 'Seller withdrew it',
  lost_price_raised: 'Seller raised the price',
  lost_no_response: 'Seller never answered',
  lost_other: 'Other',

  scam_advance_payment: 'Wanted money up front',
  scam_no_inspection: 'Refused an inspection',
  scam_stolen_photos: 'Stolen photographs',
  scam_impossible_price: 'Price is not possible',
  scam_other: 'Other',

  duplicate_same_listing: 'The same listing',
  duplicate_reposted: 'Reposted',
  duplicate_same_seller: 'Same seller, same structure',

  reopen_correction: 'Closed by mistake',
  reopen_seller_returned: 'The seller came back',
  reopen_new_information: 'New information',
} as const

/** The seeded set. A reason added to `decision_reasons` later still renders. */
type ReasonCode = keyof typeof REASON_EN

const REASON_ES: Record<ReasonCode, string> = {
  pass_too_expensive: 'Muy caro',
  pass_transport_too_expensive: 'El transporte cuesta demasiado',
  pass_bad_condition: 'Está en muy malas condiciones',
  pass_title_issue: 'Problema con el título',
  pass_too_large: 'Muy grande',
  pass_too_small: 'Muy pequeño',
  pass_poor_access: 'No se puede meter al terreno',
  pass_seller_unreliable: 'El vendedor no es confiable',
  pass_sold: 'Ya se vendió',
  pass_scam_concern: 'Parece una estafa',
  pass_found_better: 'Encontramos uno mejor',
  pass_other: 'Otro',

  finalist_excellent_landed_cost: 'Excelente costo puesto en el terreno',
  finalist_nearby: 'Está cerca',
  finalist_good_condition: 'En buenas condiciones',
  finalist_easy_transport: 'Fácil de mover',
  finalist_excellent_size: 'Del tamaño correcto',
  finalist_utilities_included: 'Trae los servicios',
  finalist_strong_seller: 'Vendedor derecho',
  finalist_move_in_ready: 'Listo para mudarse',

  lost_sold_to_other: 'Se la vendieron a otro',
  lost_seller_withdrew: 'El vendedor la retiró',
  lost_price_raised: 'El vendedor subió el precio',
  lost_no_response: 'El vendedor nunca contestó',
  lost_other: 'Otro',

  scam_advance_payment: 'Pidió dinero por adelantado',
  scam_no_inspection: 'No dejó inspeccionar',
  scam_stolen_photos: 'Fotos robadas',
  scam_impossible_price: 'Ese precio no es posible',
  scam_other: 'Otro',

  duplicate_same_listing: 'El mismo anuncio',
  duplicate_reposted: 'Republicado',
  duplicate_same_seller: 'Mismo vendedor, misma estructura',

  reopen_correction: 'Se cerró por error',
  reopen_seller_returned: 'El vendedor volvió',
  reopen_new_information: 'Información nueva',
}

const REASON_TABLE: Table<ReasonCode> = { en: REASON_EN, 'es-PR': REASON_ES }
export const reasonLabel = (code: string, locale: Locale): string => look(REASON_TABLE, locale, code)

/* ────────────────────────────────────────────────────────────────────────────
 * The listing vocabulary
 * ────────────────────────────────────────────────────────────────────────── */

const STRUCTURE_TABLE: Table<StructureType> = {
  en: {
    travel_trailer: 'Travel trailer',
    fifth_wheel: 'Fifth wheel',
    motorhome: 'Motorhome',
    mobile_home: 'Mobile home',
    modular_home: 'Modular home',
    park_model: 'Park model',
    container: 'Container',
    cargo_trailer: 'Cargo trailer',
    tiny_house: 'Tiny house',
    casita: 'Casita',
    other: 'Other',
  },
  'es-PR': {
    travel_trailer: 'Tráiler de viaje',
    fifth_wheel: 'Fifth wheel',
    motorhome: 'Casa rodante',
    mobile_home: 'Casa móvil',
    modular_home: 'Casa modular',
    park_model: 'Park model',
    container: 'Contenedor',
    cargo_trailer: 'Tráiler de carga',
    tiny_house: 'Casa pequeña',
    casita: 'Casita',
    other: 'Otro',
  },
}
export const structureLabel = (kind: string, locale: Locale): string => look(STRUCTURE_TABLE, locale, kind)

const CONDITION_TABLE: Table<ConditionGrade> = {
  en: {
    project: 'Project',
    poor: 'Poor',
    fair: 'Fair',
    good: 'Good',
    very_good: 'Very good',
    excellent: 'Excellent',
  },
  'es-PR': {
    project: 'Para arreglar',
    poor: 'Mala',
    fair: 'Regular',
    good: 'Buena',
    very_good: 'Muy buena',
    excellent: 'Excelente',
  },
}
export const conditionLabel = (grade: string, locale: Locale): string => look(CONDITION_TABLE, locale, grade)

/** `not_required` is a real answer: a container never had a title. */
const TITLE_TABLE: Table<TitleStatus> = {
  en: {
    clean: 'Clean title',
    in_hand: 'Title in hand',
    not_required: 'No title needed',
    bonded: 'Bonded title',
    lien: 'Lien on the title',
    salvage: 'Salvage title',
    lost: 'Title lost',
    none: 'No title',
    unknown: 'Title unknown',
  },
  'es-PR': {
    clean: 'Título limpio',
    in_hand: 'Título en mano',
    not_required: 'No necesita título',
    bonded: 'Título con fianza',
    lien: 'Título con gravamen',
    salvage: 'Título de rescate',
    lost: 'Título perdido',
    none: 'Sin título',
    unknown: 'Título desconocido',
  },
}
export const titleStatusLabel = (status: string, locale: Locale): string => look(TITLE_TABLE, locale, status)

const AVAILABILITY_TABLE: Table<Availability> = {
  en: {
    active: 'Listed',
    unknown: 'Unknown',
    pending: 'Sale pending',
    sold: 'Marked sold',
    expired: 'Expired',
    removed: 'Taken down',
    reposted: 'Reposted',
  },
  'es-PR': {
    active: 'Publicado',
    unknown: 'Sin saber',
    pending: 'Venta pendiente',
    sold: 'Marcado vendido',
    expired: 'Vencido',
    removed: 'Lo quitaron',
    reposted: 'Republicado',
  },
}
export const availabilityLabel = (availability: string, locale: Locale): string =>
  look(AVAILABILITY_TABLE, locale, availability)

const PROVENANCE_TABLE: Table<Provenance> = {
  en: {
    SOURCE_LISTING: 'From the listing',
    SELLER_MESSAGE: 'Seller said, in writing',
    PHONE_CALL: 'Seller said, on the phone',
    USER_OBSERVATION: 'We saw it',
    INSPECTION: 'From the inspection',
    DOCUMENT: 'From a document',
    AI_INFERENCE: 'Inferred by a model',
    SYSTEM_ESTIMATE: 'Estimated by the app',
    MANUAL_ESTIMATE: 'Estimated by hand',
    VERIFIED_RECORD: 'From an official record',
  },
  'es-PR': {
    SOURCE_LISTING: 'Del anuncio',
    SELLER_MESSAGE: 'Lo escribió el vendedor',
    PHONE_CALL: 'Lo dijo el vendedor por teléfono',
    USER_OBSERVATION: 'Lo vimos',
    INSPECTION: 'De la inspección',
    DOCUMENT: 'De un documento',
    AI_INFERENCE: 'Deducido por un modelo',
    SYSTEM_ESTIMATE: 'Estimado por la app',
    MANUAL_ESTIMATE: 'Estimado a mano',
    VERIFIED_RECORD: 'De un registro oficial',
  },
}
export const provenanceLabel = (provenance: string, locale: Locale): string =>
  look(PROVENANCE_TABLE, locale, provenance)

/**
 * Coerce whatever the server sent into a `Certainty`.
 *
 * The certainty is COMPUTED ON THE SERVER (`facts.certainty`) and travels on
 * every fact, so nothing here re-derives it from a provenance — that mapping
 * enforces a rule, and a second copy of a rule is a copy that drifts.
 */
export function asCertainty(value: unknown): Certainty {
  const certainties: readonly string[] = [
    'VERIFIED',
    'SOURCE_FACT',
    'SELLER_CLAIM',
    'OUR_ESTIMATE',
    'AI_INFERENCE',
    'UNKNOWN',
  ]
  return typeof value === 'string' && certainties.includes(value) ? (value as Certainty) : 'UNKNOWN'
}

const FACT_TABLE: Table<FactAttribute> = {
  en: {
    structure_type: 'Kind of structure',
    year: 'Year',
    manufacturer: 'Make',
    model: 'Model',
    length_ft: 'Length',
    width_ft: 'Width',
    height_ft: 'Height',
    square_feet: 'Square feet',
    weight_lbs: 'Weight',
    bedrooms: 'Bedrooms',
    bathrooms: 'Bathrooms',
    condition: 'Condition',
    roof_condition: 'Roof',
    known_damage: 'Known damage',
    title_status: 'Title',
    vin: 'VIN or serial',
    towable: 'Towable',
    wheels_present: 'Wheels',
    axles_present: 'Axles',
    hitch_present: 'Hitch',
    has_electric: 'Electric',
    has_plumbing: 'Plumbing',
    has_water: 'Water',
    has_septic: 'Septic',
    has_ac: 'Air conditioning',
  },
  'es-PR': {
    structure_type: 'Tipo de estructura',
    year: 'Año',
    manufacturer: 'Marca',
    model: 'Modelo',
    length_ft: 'Largo',
    width_ft: 'Ancho',
    height_ft: 'Alto',
    square_feet: 'Pies cuadrados',
    weight_lbs: 'Peso',
    bedrooms: 'Cuartos',
    bathrooms: 'Baños',
    condition: 'Condición',
    roof_condition: 'Techo',
    known_damage: 'Daños conocidos',
    title_status: 'Título',
    vin: 'VIN o serie',
    towable: 'Se puede remolcar',
    wheels_present: 'Gomas',
    axles_present: 'Ejes',
    hitch_present: 'Enganche',
    has_electric: 'Electricidad',
    has_plumbing: 'Plomería',
    has_water: 'Agua',
    has_septic: 'Pozo séptico',
    has_ac: 'Aire acondicionado',
  },
}
export const factLabel = (attribute: string, locale: Locale): string => look(FACT_TABLE, locale, attribute)

const UTILITY_TABLE: Table<UtilityKey> = {
  en: {
    plumbing: 'Plumbing',
    water: 'Water',
    electric: 'Electric',
    ac: 'Air conditioning',
    septic: 'Septic',
    kitchen: 'Kitchen',
    bathroom: 'Bathroom',
  },
  'es-PR': {
    plumbing: 'Plomería',
    water: 'Agua',
    electric: 'Electricidad',
    ac: 'Aire acondicionado',
    septic: 'Pozo séptico',
    kitchen: 'Cocina',
    bathroom: 'Baño',
  },
}
export const utilityLabel = (key: string, locale: Locale): string => look(UTILITY_TABLE, locale, key)

const DAMAGE_TABLE: Table<DamageFlag> = {
  en: { roof: 'Roof', floor: 'Floor', water_damage: 'Water damage', mold: 'Mould' },
  'es-PR': { roof: 'Techo', floor: 'Piso', water_damage: 'Daño de agua', mold: 'Moho' },
}
export const damageLabel = (flag: string, locale: Locale): string => look(DAMAGE_TABLE, locale, flag)

/* ────────────────────────────────────────────────────────────────────────────
 * Money — the twelve cost categories
 * ────────────────────────────────────────────────────────────────────────── */

const COST_TABLE: Table<CostCategory> = {
  en: {
    purchase: 'Purchase price',
    taxes_fees: 'Taxes and fees',
    road_transport_origin: 'Road haul on the mainland',
    ferry: 'Barge',
    road_transport_island: 'Road haul on the island',
    handling: 'Loading and handling',
    permits: 'Permits',
    repairs: 'Repairs',
    placement: 'Pad and levelling',
    utilities: 'Utility hookups',
    contingency: 'Contingency',
    other: 'Other',
  },
  'es-PR': {
    purchase: 'Precio de compra',
    taxes_fees: 'Impuestos y cargos',
    road_transport_origin: 'Acarreo en la isla grande',
    ferry: 'Barcaza',
    road_transport_island: 'Acarreo en la isla',
    handling: 'Carga y manejo',
    permits: 'Permisos',
    repairs: 'Reparaciones',
    placement: 'Base y nivelación',
    utilities: 'Conexiones de servicios',
    contingency: 'Contingencia',
    other: 'Otro',
  },
}
export const costCategoryLabel = (category: string, locale: Locale): string => look(COST_TABLE, locale, category)

/**
 * Where a line's number came from — and therefore how much it can be trusted.
 *
 * `assumption` is the only basis a recompute may overwrite; the others carry
 * `locked = 1` in SQL, which is what makes an override survive a sweep.
 */
const BASIS_TABLE: Table<CostBasis> = {
  en: {
    assumption: 'From the assumption library',
    listing: 'From the listing',
    override: 'Set by hand',
    quote: 'From a real quote',
    manual: 'Entered by hand',
  },
  'es-PR': {
    assumption: 'De la biblioteca de supuestos',
    listing: 'Del anuncio',
    override: 'Puesto a mano',
    quote: 'De una cotización real',
    manual: 'Entrado a mano',
  },
}
export const costBasisLabel = (basis: string, locale: Locale): string => look(BASIS_TABLE, locale, basis)

/* ────────────────────────────────────────────────────────────────────────────
 * The score
 * ────────────────────────────────────────────────────────────────────────── */

const FACTOR_TABLE: Table<FactorKey> = {
  en: {
    landed_cost_vs_budget: 'Landed cost against budget',
    transportability: 'Can it be moved',
    land_fit: 'Does it fit the land',
    condition: 'Condition',
    livability: 'Livability',
    title_and_documentation: 'Title and paperwork',
    price_vs_comparables: 'Price against comparables',
    repairs_required: 'Repairs needed',
    distance_and_logistics: 'Distance and logistics',
    age: 'Age',
    profile_match: 'Matches what we want',
    seller_responsiveness: 'Seller responsiveness',
    listing_freshness: 'How long it has been listed',
  },
  'es-PR': {
    landed_cost_vs_budget: 'Costo puesto contra el presupuesto',
    transportability: 'Se puede mover',
    land_fit: 'Cabe en el terreno',
    condition: 'Condición',
    livability: 'Habitabilidad',
    title_and_documentation: 'Título y papeles',
    price_vs_comparables: 'Precio contra otros parecidos',
    repairs_required: 'Reparaciones necesarias',
    distance_and_logistics: 'Distancia y logística',
    age: 'Edad',
    profile_match: 'Coincide con lo que buscamos',
    seller_responsiveness: 'Qué tan rápido contesta el vendedor',
    listing_freshness: 'Cuánto lleva publicado',
  },
}
export const factorLabel = (key: string, locale: Locale): string => look(FACTOR_TABLE, locale, key)

const RISK_LEVEL_TABLE: Table<RiskLevel> = {
  en: { low: 'Low risk', caution: 'Caution', high: 'High risk' },
  'es-PR': { low: 'Riesgo bajo', caution: 'Precaución', high: 'Riesgo alto' },
}
export const riskLevelLabel = (level: string, locale: Locale): string => look(RISK_LEVEL_TABLE, locale, level)

const RISK_TABLE: Table<RiskKey> = {
  en: {
    advance_payment_requested: 'Asked for money up front',
    refuses_inspection: 'Will not allow an inspection',
    price_far_below_floor: 'Price is far below the floor for this type',
    price_far_below_comparables: 'Price is far below comparable listings',
    unusual_payment_method: 'Unusual payment method',
    duplicate_other_seller: 'Two different sellers for one structure',
    stock_or_reused_images: 'Stock or reused photographs',
    no_title: 'No title',
    no_vin_serial: 'No VIN or serial number',
    copied_description: 'Description copied from elsewhere',
    inconsistent_location: 'The location does not add up',
    contradictory_claims: 'The claims have changed',
    urgency_language: 'Urgency wording',
  },
  'es-PR': {
    advance_payment_requested: 'Pidió dinero por adelantado',
    refuses_inspection: 'No permite inspección',
    price_far_below_floor: 'El precio está muy por debajo del piso para este tipo',
    price_far_below_comparables: 'El precio está muy por debajo de otros parecidos',
    unusual_payment_method: 'Método de pago raro',
    duplicate_other_seller: 'Dos vendedores distintos para una sola estructura',
    stock_or_reused_images: 'Fotos de archivo o reusadas',
    no_title: 'Sin título',
    no_vin_serial: 'Sin VIN ni número de serie',
    copied_description: 'La descripción está copiada',
    inconsistent_location: 'La ubicación no cuadra',
    contradictory_claims: 'Cambió lo que dijo',
    urgency_language: 'Lenguaje de urgencia',
  },
}
export const riskLabel = (key: string, locale: Locale): string => look(RISK_TABLE, locale, key)

/* ────────────────────────────────────────────────────────────────────────────
 * Diligence, documents, transport
 * ────────────────────────────────────────────────────────────────────────── */

const DILIGENCE_STATUS_TABLE: Table<DiligenceStatus> = {
  en: { pass: 'Pass', fail: 'Fail', unknown: 'Unknown', na: 'Not applicable' },
  'es-PR': { pass: 'Pasa', fail: 'Falla', unknown: 'Sin saber', na: 'No aplica' },
}
export const diligenceStatusLabel = (status: string, locale: Locale): string =>
  look(DILIGENCE_STATUS_TABLE, locale, status)

const DILIGENCE_CATEGORY_TABLE: Table<DiligenceCategory> = {
  en: {
    legal: 'Legal',
    structure: 'Structure',
    systems: 'Systems',
    condition: 'Condition',
    transport: 'Transport',
  },
  'es-PR': {
    legal: 'Legal',
    structure: 'Estructura',
    systems: 'Sistemas',
    condition: 'Condición',
    transport: 'Transporte',
  },
}
export const diligenceCategoryLabel = (category: string, locale: Locale): string =>
  look(DILIGENCE_CATEGORY_TABLE, locale, category)

const DOC_TABLE: Table<DocType> = {
  en: {
    title: 'Title',
    registration: 'Registration',
    vin_photo: 'VIN photograph',
    serial_photo: 'Serial photograph',
    inspection_photo: 'Inspection photograph',
    listing_photo: 'Listing photograph',
    seller_screenshot: 'Screenshot of the seller',
    transport_quote: 'Transport quote',
    receipt: 'Receipt',
    agreement: 'Agreement',
    dimensions: 'Dimensions',
    manual: 'Manual',
    repair_estimate: 'Repair estimate',
    other: 'Other',
  },
  'es-PR': {
    title: 'Título',
    registration: 'Registración',
    vin_photo: 'Foto del VIN',
    serial_photo: 'Foto del número de serie',
    inspection_photo: 'Foto de la inspección',
    listing_photo: 'Foto del anuncio',
    seller_screenshot: 'Captura del vendedor',
    transport_quote: 'Cotización de transporte',
    receipt: 'Recibo',
    agreement: 'Acuerdo',
    dimensions: 'Medidas',
    manual: 'Manual',
    repair_estimate: 'Estimado de reparación',
    other: 'Otro',
  },
}
export const docTypeLabel = (docType: string, locale: Locale): string => look(DOC_TABLE, locale, docType)

const LEG_TABLE: Table<QuoteLeg> = {
  en: {
    road_origin: 'Road, mainland',
    ferry: 'Barge',
    road_island: 'Road, island',
    crane: 'Crane',
    handling: 'Handling',
    full: 'Door to door',
  },
  'es-PR': {
    road_origin: 'Carretera, isla grande',
    ferry: 'Barcaza',
    road_island: 'Carretera, isla',
    crane: 'Grúa',
    handling: 'Manejo',
    full: 'De puerta a puerta',
  },
}
export const quoteLegLabel = (leg: string, locale: Locale): string => look(LEG_TABLE, locale, leg)

const QUOTE_STATUS_TABLE: Table<QuoteStatus> = {
  en: {
    requested: 'Requested',
    quoted: 'Quoted',
    declined: 'Declined',
    accepted: 'Accepted',
    expired: 'Expired',
  },
  'es-PR': {
    requested: 'Pedida',
    quoted: 'Cotizada',
    declined: 'Rechazada',
    accepted: 'Aceptada',
    expired: 'Vencida',
  },
}
export const quoteStatusLabel = (status: string, locale: Locale): string => look(QUOTE_STATUS_TABLE, locale, status)

const PLAN_STATUS_TABLE: Table<PlanStatus> = {
  en: {
    planning: 'Planning',
    scheduled: 'Scheduled',
    in_transit: 'In transit',
    delivered: 'Delivered',
    placed: 'Placed',
  },
  'es-PR': {
    planning: 'Planificando',
    scheduled: 'Agendado',
    in_transit: 'En camino',
    delivered: 'Entregado',
    placed: 'Colocado',
  },
}
export const planStatusLabel = (status: string, locale: Locale): string => look(PLAN_STATUS_TABLE, locale, status)

/* ────────────────────────────────────────────────────────────────────────────
 * The seller
 * ────────────────────────────────────────────────────────────────────────── */

const CHANNEL_TABLE: Table<LogChannel> = {
  en: {
    call: 'Call',
    sms: 'Text',
    whatsapp: 'WhatsApp',
    marketplace: 'Marketplace message',
    email: 'Email',
    in_person: 'In person',
    note: 'Note',
  },
  'es-PR': {
    call: 'Llamada',
    sms: 'Texto',
    whatsapp: 'WhatsApp',
    marketplace: 'Mensaje del marketplace',
    email: 'Correo',
    in_person: 'En persona',
    note: 'Nota',
  },
}
export const channelLabel = (channel: string, locale: Locale): string => look(CHANNEL_TABLE, locale, channel)

const DIRECTION_TABLE: Table<Direction> = {
  en: { inbound: 'They wrote', outbound: 'We wrote', internal: 'Internal' },
  'es-PR': { inbound: 'Nos escribieron', outbound: 'Les escribimos', internal: 'Interno' },
}
export const directionLabel = (direction: string, locale: Locale): string => look(DIRECTION_TABLE, locale, direction)

const OUTCOME_TABLE: Table<ContactOutcome> = {
  en: {
    reached: 'Reached them',
    no_answer: 'No answer',
    voicemail: 'Left a voicemail',
    busy: 'Busy',
    wrong_number: 'Wrong number',
  },
  'es-PR': {
    reached: 'Los conseguimos',
    no_answer: 'No contestaron',
    voicemail: 'Dejamos mensaje',
    busy: 'Ocupado',
    wrong_number: 'Número equivocado',
  },
}
export const outcomeLabel = (outcome: string, locale: Locale): string => look(OUTCOME_TABLE, locale, outcome)

/* ────────────────────────────────────────────────────────────────────────────
 * Access — the measurements, and what a finding is about
 *
 * `AccessFinding.message` is an English sentence written by `shared/access.ts`
 * and it names the actual numbers, which is what makes a warning arguable
 * ("the gate is 10.0 ft, the rig is 8.0 ft"). It is rendered as written in
 * English. In Spanish the SUBJECT is translated from the code and the numbers
 * are re-rendered — §10.2 says the client may do exactly that, and it never
 * has to parse the sentence.
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * The land and envelope keys, by the name somebody would write on a tape.
 *
 * This table is what makes the setup screen's "Measure these" list readable:
 * `rollUp().missing` returns these keys, in the order the rules care about.
 */
const MEASUREMENT_EN = {
  roadAccess: 'What the road is made of',
  roadWidthFt: 'Road width',
  gateWidthFt: 'Gate width',
  gateHeightFt: 'Gate height',
  overheadClearanceFt: 'Overhead clearance (wires, branches)',
  turningRadiusFt: 'Turning room at the gate',
  maxLengthFt: 'Longest thing the pad takes',
  maxWidthFt: 'Widest thing the pad takes',
  maxHeightFt: 'Tallest thing the pad takes',
  maxWeightLb: 'Heaviest thing the ground takes',
  slopePct: 'Slope of the pad',
  groundCondition: 'What the ground is like',
  craneAccess: 'Can a crane reach the pad',
  lengthFt: 'Length of the structure',
  widthFt: 'Width of the structure',
  heightFt: 'Height of the structure',
  weightLb: 'Weight of the structure',
  craneRequired: 'Does it need a crane',
  towable: 'Does it roll on its own wheels',
  dimsSource: 'Where the measurements came from',
  drainageNotes: 'How the water runs off',
  floodNotes: 'Flooding',
  notes: 'Anything else about the land',
} as const

type MeasurementKey = keyof typeof MEASUREMENT_EN

const MEASUREMENT_ES: Record<MeasurementKey, string> = {
  roadAccess: 'De qué es el camino',
  roadWidthFt: 'Ancho del camino',
  gateWidthFt: 'Ancho del portón',
  gateHeightFt: 'Alto del portón',
  overheadClearanceFt: 'Espacio por arriba (cables, ramas)',
  turningRadiusFt: 'Espacio para virar en el portón',
  maxLengthFt: 'Lo más largo que aguanta la base',
  maxWidthFt: 'Lo más ancho que aguanta la base',
  maxHeightFt: 'Lo más alto que aguanta la base',
  maxWeightLb: 'Lo más pesado que aguanta el terreno',
  slopePct: 'Inclinación de la base',
  groundCondition: 'Cómo está el terreno',
  craneAccess: 'Si una grúa puede llegar a la base',
  lengthFt: 'Largo de la estructura',
  widthFt: 'Ancho de la estructura',
  heightFt: 'Alto de la estructura',
  weightLb: 'Peso de la estructura',
  craneRequired: 'Si necesita grúa',
  towable: 'Si rueda en sus propias gomas',
  dimsSource: 'De dónde salieron las medidas',
  drainageNotes: 'Cómo corre el agua',
  floodNotes: 'Inundaciones',
  notes: 'Cualquier otra cosa del terreno',
}

const MEASUREMENT_TABLE: Table<MeasurementKey> = { en: MEASUREMENT_EN, 'es-PR': MEASUREMENT_ES }
export const measurementLabel = (key: string, locale: Locale): string => look(MEASUREMENT_TABLE, locale, key)

const ACCESS_CODE_TABLE: Table<AccessCode> = {
  en: {
    road_none: 'There is no road to the land',
    road_access_unknown: 'Nobody has recorded what the road is',
    width_gate: 'Too wide for the gate',
    width_gate_unknown: 'The gate has not been measured',
    width_road: 'Too wide for the road',
    width_road_unknown: 'The road has not been measured',
    height_gate: 'Too tall for the gate',
    height_gate_unknown: 'The gate height has not been measured',
    height_overhead: 'Too tall for the overhead clearance',
    height_overhead_unknown: 'The overhead clearance has not been measured',
    turning: 'Not enough room to turn it in',
    turning_unknown: 'The turning room has not been measured',
    max_length: 'Longer than the pad takes',
    max_length_unknown: 'The pad length has not been measured',
    max_width: 'Wider than the pad takes',
    max_width_unknown: 'The pad width has not been measured',
    max_height: 'Taller than the pad takes',
    max_height_unknown: 'The pad height limit has not been measured',
    max_weight: 'Heavier than the ground takes',
    max_weight_unknown: 'The ground weight limit is not recorded',
    slope: 'The pad is too steep',
    slope_unknown: 'The slope has not been measured',
    crane_no_access: 'It needs a crane and a crane cannot reach',
    crane_limited_access: 'It needs a crane and crane access is limited',
    crane_access_unknown: 'Nobody has recorded whether a crane can reach',
    dims_unmeasured: 'The structure has not been measured',
  },
  'es-PR': {
    road_none: 'No hay camino hasta el terreno',
    road_access_unknown: 'Nadie ha anotado cómo es el camino',
    width_gate: 'Muy ancho para el portón',
    width_gate_unknown: 'El portón no se ha medido',
    width_road: 'Muy ancho para el camino',
    width_road_unknown: 'El camino no se ha medido',
    height_gate: 'Muy alto para el portón',
    height_gate_unknown: 'El alto del portón no se ha medido',
    height_overhead: 'Muy alto para el espacio de arriba',
    height_overhead_unknown: 'El espacio de arriba no se ha medido',
    turning: 'No hay espacio suficiente para virarlo',
    turning_unknown: 'El espacio para virar no se ha medido',
    max_length: 'Más largo de lo que aguanta la base',
    max_length_unknown: 'El largo de la base no se ha medido',
    max_width: 'Más ancho de lo que aguanta la base',
    max_width_unknown: 'El ancho de la base no se ha medido',
    max_height: 'Más alto de lo que aguanta la base',
    max_height_unknown: 'El límite de alto de la base no se ha medido',
    max_weight: 'Más pesado de lo que aguanta el terreno',
    max_weight_unknown: 'El límite de peso del terreno no está anotado',
    slope: 'La base está muy inclinada',
    slope_unknown: 'La inclinación no se ha medido',
    crane_no_access: 'Necesita grúa y la grúa no puede llegar',
    crane_limited_access: 'Necesita grúa y el acceso de la grúa es limitado',
    crane_access_unknown: 'Nadie ha anotado si una grúa puede llegar',
    dims_unmeasured: 'La estructura no se ha medido',
  },
}
export const accessCodeLabel = (code: string, locale: Locale): string => look(ACCESS_CODE_TABLE, locale, code)

/**
 * What a finding says, in the reader's language.
 *
 * English gets `shared/access.ts`'s own sentence, numbers and all — it is
 * written to be arguable and there is no reason to weaken it. Spanish gets the
 * translated subject plus the same numbers, which is §10.2's stated plan.
 */
export function accessFindingText(
  finding: { code: string; message: string; measured: number | null; limit: number | null; required: number | null; unit: string | null },
  locale: Locale,
): string {
  if (locale === 'en') return finding.message
  const subject = accessCodeLabel(finding.code, locale)
  const unit = finding.unit === 'lb' ? 'lb' : finding.unit === 'pct' ? '%' : finding.unit === 'ft' ? 'pies' : ''
  const numbers = [
    finding.measured === null ? null : `${finding.measured} ${unit}`.trim(),
    finding.required === null ? null : `necesita ${finding.required} ${unit}`.trim(),
    finding.limit === null ? null : `límite ${finding.limit} ${unit}`.trim(),
  ].filter((part): part is string => part !== null)
  return numbers.length ? `${subject} — ${numbers.join(', ')}.` : `${subject}.`
}
